import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useMemo } from 'react';
import styled from 'styled-components';

import { StatsIcon as DumbStatsIcon } from '@sorare/core/src/components/draft/StatsIcon';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { normalizeStatsFromRawScores } from '@sorare/core/src/lib/performance';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { Color } from '@sorare/core/src/style/types';

import { AnyPlayerFifteenStatsIcon } from '#src/components/player/AnyPlayerFifteenStatsIcon/index.tsx';
import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';
import { useTeamMode } from '#src/searchCommonPlayer/teamMode.ts';

import type { AnyStatsIconCell_anyPlayer } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  width: var(--player-stats-icon-width);
  flex-shrink: 0;
  display: none;
  &.showOnMobile {
    display: block;
  }
  @media ${laptopAndAbove} {
    display: block;
  }
`;

const GREY_BAR_COLOR: Color = 'var(--c-nd-300)';

type Props = {
  anyPlayer: AnyStatsIconCell_anyPlayer;
  onStatsClick: () => void;
  showOnMobile?: boolean;
  /**
   * 15 most-recent per-game team slugs aligned with the bars. When set, the
   * cell greys out bars whose team doesn't match the active `?tm=` mode.
   * Supplied by team-mode-aware tables (scouting center, compose bench).
   */
  recentTeamSlugs?: (string | null)[];
};

export const AnyStatsIconCell = ({
  anyPlayer,
  onStatsClick,
  showOnMobile,
  recentTeamSlugs,
}: Props) => {
  const isLaptop = useIsLaptop();
  const teamMode = useTeamMode();
  const targetTeamSlug =
    teamMode === 'CLUB'
      ? anyPlayer.activeClub?.slug
      : teamMode === 'NATIONAL_TEAM'
        ? anyPlayer.activeNationalTeam?.slug
        : null;

  const useTeamAwarePath = !!recentTeamSlugs && !!targetTeamSlug;

  const stats = useMemo(() => {
    if (!useTeamAwarePath) return null;
    const size = isLaptop ? 15 : 5;
    const allFifteen = anyPlayer.rawFifteenPlayerGameScores ?? [];
    const rawScores = isLaptop ? allFifteen : allFifteen.slice(0, size);
    const baseStats = normalizeStatsFromRawScores(
      rawScores,
      anyPlayer.sport,
      size
    );
    // normalizeStatsFromRawScores pads at start and reverses to put oldest
    // first — apply the same transform to team slugs to keep them aligned.
    const sliced = (recentTeamSlugs ?? []).slice(0, size);
    const orderedTeamSlugs = [
      ...new Array(size - sliced.length).fill(null),
      ...sliced.slice().reverse(),
    ];
    return baseStats.map((s, i) =>
      orderedTeamSlugs[i] === targetTeamSlug
        ? s
        : { ...s, color: GREY_BAR_COLOR }
    );
  }, [
    useTeamAwarePath,
    isLaptop,
    anyPlayer.rawFifteenPlayerGameScores,
    anyPlayer.sport,
    recentTeamSlugs,
    targetTeamSlug,
  ]);

  return (
    <Wrapper className={classNames({ showOnMobile })}>
      {useTeamAwarePath && stats ? (
        <DumbStatsIcon
          stats={stats}
          onClick={onStatsClick}
          width={isLaptop ? 10 : 4}
          height={isLaptop ? 4 : 3}
        />
      ) : isLaptop ? (
        <AnyPlayerFifteenStatsIcon player={anyPlayer} onClick={onStatsClick} />
      ) : (
        <AnyPlayerStatsIcon player={anyPlayer} onClick={onStatsClick} />
      )}
    </Wrapper>
  );
};

AnyStatsIconCell.fragments = {
  anyPlayer: gql`
    fragment AnyStatsIconCell_anyPlayer on AnyPlayerInterface {
      slug
      sport
      activeClub {
        slug
      }
      activeNationalTeam {
        slug
      }
      ...AnyPlayerStatsIcon_anyPlayer
      ...AnyPlayerFifteenStatsIcon_anyPlayer
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${AnyPlayerFifteenStatsIcon.fragments.anyPlayer}
  ` as TypedDocumentNode<AnyStatsIconCell_anyPlayer>,
};
